import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AIPromptDataRequest } from '../../../../../../../libs/shared/src/lib/models/ai-prompt-data-request/ai-prompt-data-request.model';
import { TranslateModule } from '@ngx-translate/core';
import { APIService, NotificationService, AnthropicService } from '@fourcomply-admin/shared';
import { ConversationRequest, PageGenerationResult, AttachedImage } from '../../../../../../../libs/shared/src/lib/models/anthropic/anthropic.model';
import { AgentRequest, AgentResponse, AgentSuggestionResponse, AutocompleteItem, PendingAgentConfirmation } from '../../../../../../../libs/shared/src/lib/models/agent/agent.model';
import { AIAgentService } from '../../../../../../../libs/shared/src/lib/services/api/agent/ai-agent.service';
import { AiPanelService, AiPanelContext, AiContextType } from '../../services/ai-panel.service';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  formattedContent?: string;
  timestamp: string; // ISO string for serialization
}

interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  updatedAt: string;
}

const STORAGE_KEY = '4comply_ai_sessions';
const STORAGE_SESSION_START = '4comply_ai_session_start';
const MAX_SESSIONS = 20;

@Component({
  selector: 'app-ai-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './ai-panel.component.html',
  styleUrls: ['./ai-panel.component.scss']
})
export class AiPanelComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @ViewChild('messagesContainer') messagesContainer: ElementRef;
  @ViewChild('previewIframe') previewIframe: ElementRef<HTMLIFrameElement>;

  // Panel state
  aiPanelOpen: boolean = false;
  showPreviewPanel: boolean = false;
  isGenerating: boolean = false;
  aiPrompt: string = '';
  aiPage: any = null;
  shouldHidePanel: boolean = false;
  showHistory: boolean = false;
  showTokenDropdown: boolean = false;
  private currentUrl: string = '';

  // Token usage
  sessionTokens: number = 0;
  userTotalTokens: number = 0;
  private sessionStartTime: string;

  // Chat
  messages: ChatMessage[] = [];
  chatSessions: ChatSession[] = [];
  currentSessionId: string | null = null;

  // Preview
  generatedHtml: SafeHtml;

  // Data preview panel
  showDataPreview: boolean = false;
  dataPreviewTitle: string = '';
  dataPreviewColumns: string[] = [];
  dataPreviewRows: any[] = [];

  // Page editor mode (Anthropic)
  anthropicConversationId: string | null = null;
  pendingPageChanges: PageGenerationResult | null = null;
  showPagePreview: boolean = false;
  pagePreviewTab: 'preview' | 'code' = 'preview';
  pagePreviewHtml: string = '';
  pendingPreviewPageId: string | null = null;
  isNewPagePreview: boolean = false;

  // Image upload (page-editor mode)
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
  attachedImages: AttachedImage[] = [];
  maxImages: number = 5;

  // Speech-to-text
  isListening: boolean = false;
  private speechRecognition: any = null;

  // Context
  activeContext: AiPanelContext = { type: 'general' };

  // Agent mode
  agentConversationId: string | null = null;
  pendingAgentConfirmation: PendingAgentConfirmation | null = null;

  // Suggestions & autocomplete
  suggestions: AgentSuggestionResponse | null = null;
  suggestionsLoaded: boolean = false;
  autocompleteItems: AutocompleteItem[] = [];
  showAutocomplete: boolean = false;
  autocompleteIndex: number = -1;

  // Quick prompts - page editor context
  pageEditorQuickPrompts = [
    {
      label: 'Add a control',
      prompt: 'Add a new core preference control to the page',
      icon: 'fa-plus-circle'
    },
    {
      label: 'Update styles',
      prompt: 'Update the visual style of the page with a modern look',
      icon: 'fa-palette'
    },
    {
      label: 'Make responsive',
      prompt: 'Make the page responsive for all devices',
      icon: 'fa-mobile-alt'
    },
    {
      label: 'Add JavaScript',
      prompt: 'Add JavaScript functionality to the page',
      icon: 'fa-code'
    }
  ];

  // Quick prompts
  quickPrompts = [
    {
      label: 'Configuration summary',
      prompt: 'Show me a summary of the compliance configuration',
      icon: 'fa-cogs'
    },
    {
      label: 'View regulations',
      prompt: 'Show me all configured regulations',
      icon: 'fa-gavel'
    },
    {
      label: 'Permission types',
      prompt: 'List all permission types',
      icon: 'fa-list'
    },
    {
      label: 'Query permissions by email',
      prompt: 'Show me the permissions for user@example.com',
      icon: 'fa-shield-alt'
    },
    {
      label: 'Query compliance inputs',
      prompt: 'Show me the compliance inputs for user@example.com',
      icon: 'fa-clipboard-check'
    },
    {
      label: 'Create a preference page',
      prompt: 'Create a preference page with email marketing consent',
      icon: 'fa-file-alt'
    }
  ];

  get isPageEditorMode(): boolean {
    return this.activeContext?.type === 'page-editor';
  }

  get activeQuickPrompts(): any[] {
    return this.isPageEditorMode ? this.pageEditorQuickPrompts : this.quickPrompts;
  }

  constructor(
    private api: APIService,
    private notification: NotificationService,
    private sanitizer: DomSanitizer,
    private aiPanelService: AiPanelService,
    private router: Router,
    private anthropicService: AnthropicService,
    private aiAgentService: AIAgentService
  ) { }

  ngOnInit(): void {
    // Restore or initialize session start time for token tracking
    const stored = localStorage.getItem(STORAGE_SESSION_START);
    this.sessionStartTime = stored || new Date().toISOString();
    if (!stored) {
      localStorage.setItem(STORAGE_SESSION_START, this.sessionStartTime);
    }

    this.aiPanelService.panelState$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(state => {
      this.aiPanelOpen = state.isOpen;
      if (state.isOpen && !this.suggestionsLoaded) {
        this.loadSuggestions();
      }
    });

    this.aiPanelService.context$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(context => {
      const previousType = this.activeContext?.type;
      this.activeContext = context;
      // Reset Anthropic conversation when leaving page-editor mode
      if (previousType === 'page-editor' && context.type !== 'page-editor') {
        this.anthropicConversationId = null;
        this.pendingPageChanges = null;
        this.showPagePreview = false;
        this.isNewPagePreview = false;
        this.pendingPreviewPageId = null;
      }
      // Auto-show render preview for newly created preference pages
      if (context.type === 'page-editor' && this.pendingPreviewPageId && context.pageId === this.pendingPreviewPageId) {
        const pageId = this.pendingPreviewPageId;
        this.pendingPreviewPageId = null;
        this.api.preferencePage.getPreviewHtml(pageId, context.tenantId).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: (html: string) => {
            this.pagePreviewHtml = html;
            this.isNewPagePreview = true;
            this.showPagePreview = true;
            this.writePreviewIframe();
          }
        });
      }
    });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const prevRoot = this.getRootSegment(this.currentUrl);
          const nextRoot = this.getRootSegment(event.urlAfterRedirects);
          if (prevRoot !== nextRoot && this.aiPanelOpen) {
            this.closePanel();
          }
          this.currentUrl = event.urlAfterRedirects;
          this.checkRouteAndHidePanel(event.url);
        }
      });

    this.api.aiTokenUsage.userTotal$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(summary => {
      this.userTotalTokens = summary?.totalTokens ?? 0;
    });

    this.currentUrl = this.router.url;
    this.api.aiTokenUsage.getUserSummary();
    this.refreshSessionTokens();
    this.loadSessionsFromStorage();
    this.checkRouteAndHidePanel(this.router.url);

    // Listen for data preview open from chat buttons
    this._openPreviewHandler = () => { this.showDataPreview = true; };
    document.addEventListener('ai-open-data-preview', this._openPreviewHandler);
  }

  private _openPreviewHandler: (() => void) | null = null;

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this._openPreviewHandler) {
      document.removeEventListener('ai-open-data-preview', this._openPreviewHandler);
    }
  }

  private getRootSegment(url: string): string {
    return url.split('?')[0].split('/')[1] ?? '';
  }

  private checkRouteAndHidePanel(url: string): void {
    // No longer hide on preference-page routes — the panel now handles page-editor context
    this.shouldHidePanel = false;
  }

  closePanel(): void {
    this.aiPanelOpen = false;
    this.showTokenDropdown = false;
    this.aiPanelService.setPanelState({ isOpen: false });
  }

  toggleHistory(): void {
    this.showHistory = !this.showHistory;
  }

  newChat(): void {
    this.saveCurrentSession();
    this.currentSessionId = null;
    this.messages = [];
    this.showHistory = false;
    this.showPreviewPanel = false;
    this.showDataPreview = false;
    this.aiPage = null;
  }

  loadSession(sessionId: string): void {
    this.saveCurrentSession();
    const session = this.chatSessions.find(s => s.id === sessionId);
    if (session) {
      this.currentSessionId = session.id;
      this.messages = session.messages.map(m => ({
        ...m,
        formattedContent: m.role === 'assistant' ? this.formatMessage(m.content) : undefined
      }));
      this.showHistory = false;
      this.scrollToBottom();
    }
  }

  deleteSession(sessionId: string, event: Event): void {
    event.stopPropagation();
    if (this.currentSessionId === sessionId) {
      this.messages = [];
      this.currentSessionId = null;
    }
    this.chatSessions = this.chatSessions.filter(s => s.id !== sessionId);
    this.saveSessionsToStorage();
  }

  trackBySessionId(index: number, session: ChatSession): string {
    return session.id;
  }

  handleKeyDown(event: KeyboardEvent): void {
    // Autocomplete keyboard navigation
    if (this.showAutocomplete && this.autocompleteItems.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.autocompleteIndex = Math.min(this.autocompleteIndex + 1, this.autocompleteItems.length - 1);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.autocompleteIndex = Math.max(this.autocompleteIndex - 1, 0);
        return;
      }
      if (event.key === 'Enter' && this.autocompleteIndex >= 0) {
        event.preventDefault();
        this.selectAutocompleteItem(this.autocompleteItems[this.autocompleteIndex]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        this.showAutocomplete = false;
        this.autocompleteIndex = -1;
        return;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  sendQuickPrompt(prompt: string): void {
    this.aiPrompt = prompt;
    this.sendMessage();
  }

  sendMessage(): void {
    if (!this.aiPrompt?.trim() || this.isGenerating) return;

    const userMessage = this.aiPrompt.trim();
    this.aiPrompt = '';

    // Add user message
    this.messages.push({
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    });
    this.scrollToBottom();

    this.isGenerating = true;

    // Route to Anthropic if in page-editor mode
    if (this.isPageEditorMode) {
      this.sendPageEditorMessage(userMessage);
      return;
    }

    // Route to AI Agent (write infrastructure) for general mode
    this.sendAgentMessage(userMessage);
  }

  /**
   * Send message through AI Agent write infrastructure
   */
  private sendAgentMessage(userMessage: string): void {
    const request: AgentRequest = {
      conversationId: this.agentConversationId ?? undefined,
      userMessage: userMessage,
    };

    this.aiAgentService.handleTurn(request).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: AgentResponse) => {
        this.isGenerating = false;
        this.agentConversationId = response.conversationId;

        if (response.requiresConfirmation) {
          this.pendingAgentConfirmation = {
            summary: response.pendingConfirmationSummary ?? response.assistantMessage,
            conversationId: response.conversationId,
          };
        }

        if (response.assistantMessage) {
          this.messages.push({
            role: 'assistant',
            content: response.assistantMessage,
            formattedContent: this.formatMessage(response.assistantMessage),
            timestamp: new Date().toISOString(),
          });
        }

        if (response.createdEntities?.length) {
          const entityList = response.createdEntities
            .map((e: { entityType: string; name: string }) => `${e.entityType}: ${e.name}`)
            .join(', ');
          this.messages.push({
            role: 'assistant',
            content: `Created: ${entityList}`,
            formattedContent: `<strong>Created:</strong> ${entityList}`,
            timestamp: new Date().toISOString(),
          });
          const createdPage = response.createdEntities.find(
            (e: { entityType: string; entityId?: string }) => e.entityType === 'PreferencePage'
          );
          if (createdPage?.entityId) {
            this.pendingPreviewPageId = createdPage.entityId;
            this.aiPanelService.navigate('/preference-page/' + createdPage.entityId);
          }
        }

        if (!response.success && response.errorMessage) {
          this.messages.push({
            role: 'assistant',
            content: response.errorMessage,
            timestamp: new Date().toISOString(),
          });
        }

        this.scrollToBottom();
        this.saveCurrentSession();
      },
      error: (err: unknown) => {
        this.isGenerating = false;
        this.messages.push({
          role: 'assistant',
          content: 'An error occurred while processing your request. Please try again.',
          timestamp: new Date().toISOString(),
        });
        this.scrollToBottom();
      },
    });
  }

  /**
   * Confirm a pending agent write action
   */
  confirmAgentAction(): void {
    if (!this.pendingAgentConfirmation) return;
    this.isGenerating = true;

    this.aiAgentService.confirmAction(
      this.pendingAgentConfirmation.conversationId
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: AgentResponse) => {
        this.isGenerating = false;
        this.pendingAgentConfirmation = null;
        this.agentConversationId = response.conversationId;

        if (response.assistantMessage) {
          this.messages.push({
            role: 'assistant',
            content: response.assistantMessage,
            formattedContent: this.formatMessage(response.assistantMessage),
            timestamp: new Date().toISOString(),
          });
        }
        this.scrollToBottom();
        this.saveCurrentSession();
      },
      error: () => {
        this.isGenerating = false;
        this.pendingAgentConfirmation = null;
      },
    });
  }

  /**
   * Reject a pending agent write action
   */
  rejectAgentAction(): void {
    if (!this.pendingAgentConfirmation) return;
    this.isGenerating = true;

    this.aiAgentService.rejectAction(
      this.pendingAgentConfirmation.conversationId
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: AgentResponse) => {
        this.isGenerating = false;
        this.pendingAgentConfirmation = null;

        if (response.assistantMessage) {
          this.messages.push({
            role: 'assistant',
            content: response.assistantMessage,
            formattedContent: this.formatMessage(response.assistantMessage),
            timestamp: new Date().toISOString(),
          });
        }
        this.scrollToBottom();
        this.saveCurrentSession();
      },
      error: () => {
        this.isGenerating = false;
        this.pendingAgentConfirmation = null;
      },
    });
  }

  // ── Suggestions & Autocomplete ──

  /**
   * Load suggestions from the API (core preferences, contact fields, business units).
   * Called once when the panel first opens.
   */
  private loadSuggestions(): void {
    this.aiAgentService.getSuggestions().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: AgentSuggestionResponse) => {
        this.suggestions = response;
        this.suggestionsLoaded = true;
      },
      error: () => {
        this.suggestionsLoaded = false;
      },
    });
  }

  // Intent triggers: when the input ends with one of these, show ALL items of that category
  private readonly preferenceIntentTriggers = [
    'core preference', 'core preferences', 'preferencias', 'preferencia',
    'preference', 'preferences', 'consent', 'consentimiento',
    'newsletter', 'marketing', 'opt-in', 'opt-out', 'suscripcion',
  ];

  private readonly contactIntentTriggers = [
    'core contact', 'core contacts', 'contact field', 'contact fields',
    'campos de contacto', 'campo de contacto', 'datos personales',
    'datos de contacto', 'personal data',
  ];

  private readonly buIntentTriggers = [
    'business unit', 'business units', 'unidad de negocio', 'unidades de negocio',
  ];

  /**
   * Handle autocomplete as user types.
   * Two modes:
   * 1. Intent mode: user wrote "...con los core preferences" → show ALL core preferences
   * 2. Search mode: last word partially matches a name → show matches
   */
  onPromptInput(): void {
    if (!this.suggestionsLoaded || !this.suggestions) {
      this.showAutocomplete = false;
      return;
    }

    const text = (this.aiPrompt ?? '').toLowerCase().trimEnd();
    let items: AutocompleteItem[] = [];

    // 1. Intent detection — check if input ends with a trigger phrase
    const prefIntent = this.preferenceIntentTriggers.some(t => text.endsWith(t));
    const contactIntent = this.contactIntentTriggers.some(t => text.endsWith(t));
    const buIntent = this.buIntentTriggers.some(t => text.endsWith(t));

    if (prefIntent) {
      items = this.suggestions.corePreferences.map(p => ({
        category: 'preference' as const,
        detail: p.type ?? 'Preference',
        insert: p.name,
        label: p.name,
      }));
    } else if (contactIntent) {
      items = this.suggestions.coreContactFields.map(f => ({
        category: 'contact' as const,
        detail: f.type ?? 'Contact Field',
        insert: f.name,
        label: f.name,
      }));
    } else if (buIntent) {
      items = this.suggestions.businessUnits.map(bu => ({
        category: 'business-unit' as const,
        detail: 'Business Unit',
        insert: bu.name,
        label: bu.name,
      }));
    } else {
      // 2. Search mode — match last word against names
      const words = this.aiPrompt.split(/\s+/);
      const lastWord = (words[words.length - 1] ?? '').toLowerCase();

      if (lastWord.length >= 2) {
        this.suggestions.corePreferences.forEach(p => {
          if (p.name?.toLowerCase().includes(lastWord)) {
            items.push({ category: 'preference', detail: p.type ?? 'Preference', insert: p.name, label: p.name });
          }
        });
        this.suggestions.coreContactFields.forEach(f => {
          if (f.name?.toLowerCase().includes(lastWord)) {
            items.push({ category: 'contact', detail: f.type ?? 'Contact Field', insert: f.name, label: f.name });
          }
        });
        this.suggestions.businessUnits.forEach(bu => {
          if (bu.name?.toLowerCase().includes(lastWord)) {
            items.push({ category: 'business-unit', detail: 'Business Unit', insert: bu.name, label: bu.name });
          }
        });
      }
    }

    this.autocompleteItems = items.slice(0, 10);
    this.showAutocomplete = this.autocompleteItems.length > 0;
    this.autocompleteIndex = -1;
  }

  /**
   * Select an autocomplete item — replaces the last word in the input with the selected value.
   */
  selectAutocompleteItem(item: AutocompleteItem): void {
    const words = this.aiPrompt.split(/\s+/);
    words[words.length - 1] = item.insert;
    this.aiPrompt = words.join(' ') + ' ';
    this.showAutocomplete = false;
    this.autocompleteItems = [];
  }

  /**
   * Click a suggestion chip — appends the name to the input.
   */
  insertSuggestionChip(name: string): void {
    this.aiPrompt += (this.aiPrompt ? ' ' : '') + name;
    this.showAutocomplete = false;
  }

  /**
   * @deprecated Use sendAgentMessage for general mode. Kept for reference.
   */
  private sendLegacyPrompt(userMessage: string): void {
    // Send to general AI API
    const promptDataRequest: AIPromptDataRequest = {
      id: '',
      prompt: userMessage,
      tenantId: '',
    };

    this.api.aiPrompt.handlePrompt(promptDataRequest).subscribe({
      next: (response) => {
        this.isGenerating = false;
        this.aiPage = response;

        let assistantContent = '';

        if (response.intent === 'create_page') {
          this.generatedHtml = this.sanitizer.bypassSecurityTrustHtml(response.data.html) ?? '';
          this.showPreviewPanel = true;
          assistantContent = 'I\'ve generated a preference page based on your request. You can see the preview below and confirm to save it.';
        } else if (response.intent === 'generate_report') {
          const reportData = response.data.data ?? [];
          if (reportData.length > 0) {
            assistantContent = this.formatReportAsTable(reportData);
          } else {
            assistantContent = 'The report returned no results for the given criteria.';
          }
        } else if (response.intent === 'query_permissions') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else {
            const permData = response.data?.data ?? [];
            const email = response.data?.email ?? '';
            const total = response.data?.total ?? 0;
            if (permData.length > 0) {
              assistantContent = this.formatAsCards(permData, `Permissions for ${email}`, total);
            } else {
              assistantContent = `No permissions found for **${email}**.`;
            }
          }
        } else if (response.intent === 'query_compliance_inputs') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else {
            const inputData = response.data?.data ?? [];
            const email = response.data?.email ?? '';
            const total = response.data?.total ?? 0;
            if (inputData.length > 0) {
              assistantContent = this.formatAsCards(inputData, `Compliance Inputs for ${email}`, total);
            } else {
              assistantContent = `No compliance inputs found for **${email}**.`;
            }
          }
        } else if (response.intent === 'query_expiring_permissions') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else {
            const s = response.data?.summary ?? {};
            const exportData = response.data?.exportData ?? [];
            const total = response.data?.total ?? 0;

            // Show summary in chat
            assistantContent = `**Expiring Permissions Report**\n\n`;
            assistantContent += `**Period**: Next ${s.days ?? 30} days\n`;
            assistantContent += `**Total expiring**: ${total}\n`;
            assistantContent += `**This week**: ${s.expiring_this_week ?? 0}\n`;
            assistantContent += `**Next week**: ${s.expiring_next_week ?? 0}\n`;
            assistantContent += `**Rest of period**: ${s.expiring_rest ?? 0}\n\n`;
            assistantContent += `<button class="ai-open-preview-btn" onclick="document.dispatchEvent(new CustomEvent('ai-open-data-preview'))"><i class="fas fa-table"></i> View full data (${total} records)</button>`;

            // Store data for preview panel
            this.dataPreviewTitle = `Expiring Permissions (next ${s.days ?? 30} days)`;
            this.dataPreviewRows = exportData;
            this.dataPreviewColumns = exportData.length > 0 ? Object.keys(exportData[0]) : [];
          }
        } else if (response.intent === 'query_legal_activities') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else {
            const s = response.data?.summary ?? {};
            const exportData = response.data?.exportData ?? [];
            const total = response.data?.total ?? 0;

            assistantContent = `**Legal Activity Vault**\n\n`;
            assistantContent += `**Period**: ${s.period ?? 'N/A'}\n`;
            assistantContent += `**Total records**: ${total}\n`;
            assistantContent += `**Unique emails**: ${s.unique_emails ?? 0}\n`;
            assistantContent += `**Activity types**: ${s.activity_types ?? 0}\n\n`;
            assistantContent += `<button class="ai-open-preview-btn" onclick="document.dispatchEvent(new CustomEvent('ai-open-data-preview'))"><i class="fas fa-table"></i> View full data (${total} records)</button>`;

            this.dataPreviewTitle = `Legal Activities (${s.period ?? ''})`;
            this.dataPreviewRows = exportData;
            this.dataPreviewColumns = exportData.length > 0 ? Object.keys(exportData[0]) : [];
          }
        } else if (response.intent === 'query_legal_activity_errors') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else {
            const s = response.data?.summary ?? {};
            const exportData = response.data?.exportData ?? [];
            const total = response.data?.total ?? 0;

            assistantContent = `**Legal Activity Errors**\n\n`;
            assistantContent += `**Period**: ${s.period ?? 'N/A'}\n`;
            assistantContent += `**Total errors**: ${total}\n`;
            assistantContent += `**Unique emails**: ${s.unique_emails ?? 0}\n`;
            assistantContent += `**Reject reasons**: ${s.reject_reasons ?? 0}\n\n`;
            assistantContent += `<button class="ai-open-preview-btn" onclick="document.dispatchEvent(new CustomEvent('ai-open-data-preview'))"><i class="fas fa-table"></i> View full data (${total} records)</button>`;

            this.dataPreviewTitle = `Legal Activity Errors (${s.period ?? ''})`;
            this.dataPreviewRows = exportData;
            this.dataPreviewColumns = exportData.length > 0 ? Object.keys(exportData[0]) : [];
          }
        } else if (response.intent === 'query_contact_preferences') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else {
            const prefData = response.data?.data ?? [];
            const email = response.data?.email ?? '';
            const total = response.data?.total ?? 0;
            assistantContent = this.formatAsCards(prefData, `Contact Preferences for ${email}`, total);
          }
        } else if (response.intent === 'query_contact_preference_history') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else {
            const histData = response.data?.data ?? [];
            const email = response.data?.email ?? '';
            const total = response.data?.total ?? 0;
            assistantContent = this.formatAsCards(histData, `Preference History for ${email}`, total);
          }
        } else if (response.intent === 'query_contact_details') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else {
            const detData = response.data?.data ?? [];
            const email = response.data?.email ?? '';
            const total = response.data?.total ?? 0;
            assistantContent = this.formatAsCards(detData, `Contact Details for ${email}`, total);
          }
        } else if (response.intent === 'query_contact_details_history') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else {
            const histData = response.data?.data ?? [];
            const email = response.data?.email ?? '';
            const total = response.data?.total ?? 0;
            assistantContent = this.formatAsCards(histData, `Contact Details History for ${email}`, total);
          }
        } else if (response.intent === 'query_preference_pages') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else if (response.data?.page) {
            assistantContent = this.formatPageDetail(response.data);
          } else {
            const data = response.data?.data ?? [];
            const total = response.data?.total ?? 0;
            assistantContent = this.formatAsCards(data, 'Preference Pages', total);
          }
        } else if (response.intent === 'query_channels') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else if (response.data?.item) {
            assistantContent = this.formatItemDetail(response.data);
          } else {
            const data = response.data?.data ?? [];
            const total = response.data?.total ?? 0;
            assistantContent = this.formatAsCards(data, 'Preference Channels', total);
          }
        } else if (response.intent === 'query_frequencies') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else if (response.data?.item) {
            assistantContent = this.formatItemDetail(response.data);
          } else {
            const data = response.data?.data ?? [];
            const total = response.data?.total ?? 0;
            assistantContent = this.formatAsCards(data, 'Preference Frequencies', total);
          }
        } else if (response.intent === 'query_core_preferences') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else if (response.data?.item) {
            assistantContent = this.formatItemDetail(response.data);
          } else {
            const data = response.data?.data ?? [];
            const total = response.data?.total ?? 0;
            const title = response.data?.title || 'Core Preferences';
            assistantContent = this.formatAsCards(data, title, total);
          }
        } else if (response.intent === 'query_core_contact_fields') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else if (response.data?.item) {
            assistantContent = this.formatItemDetail(response.data);
          } else {
            const data = response.data?.data ?? [];
            const total = response.data?.total ?? 0;
            const title = response.data?.title || 'Core Contact Fields';
            assistantContent = this.formatAsCards(data, title, total);
          }
        } else if (response.intent === 'query_regulations') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else if (response.data?.regulation) {
            assistantContent = this.formatRegulationDetail(response.data);
          } else {
            const regData = response.data?.data ?? [];
            const total = response.data?.total ?? 0;
            const title = response.data?.title || 'Configured Regulations';
            assistantContent = this.formatAsCards(regData, title, total);
          }
        } else if (response.intent === 'query_permission_types') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else {
            const typeData = response.data?.data ?? [];
            const total = response.data?.total ?? 0;
            assistantContent = this.formatAsCards(typeData, 'Permission Types', total);
          }
        } else if (response.intent === 'query_permission_categories') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else {
            const catData = response.data?.data ?? [];
            const total = response.data?.total ?? 0;
            assistantContent = this.formatAsCards(catData, 'Permission Categories', total);
          }
        } else if (response.intent === 'query_systems') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else {
            const sysData = response.data?.data ?? [];
            const total = response.data?.total ?? 0;
            assistantContent = this.formatAsCards(sysData, 'Configured Systems', total);
          }
        } else if (response.intent === 'query_business_units') {
          if (response.data?.message) {
            assistantContent = response.data.message;
          } else {
            const buData = response.data?.data ?? [];
            const total = response.data?.total ?? 0;
            assistantContent = this.formatAsCards(buData, 'Business Units', total);
          }
        } else if (response.intent === 'query_tenant_info' && response.data?.message) {
          assistantContent = response.data.message;
        } else if (response.intent === 'query_config_summary' && response.data?.message) {
          assistantContent = response.data.message;
        } else if (response.intent === 'general_chat' && response.data?.message) {
          assistantContent = response.data.message;
        } else if (response.data?.message) {
          assistantContent = response.data.message;
        } else {
          assistantContent = 'Request processed successfully.';
        }

        this.messages.push({
          role: 'assistant',
          content: assistantContent,
          formattedContent: this.formatMessage(assistantContent),
          timestamp: new Date().toISOString()
        });

        // Save session and refresh tokens
        this.saveCurrentSession();
        this.refreshTokenCounts();
        this.scrollToBottom();
      },
      error: (err) => {
        this.isGenerating = false;
        this.messages.push({
          role: 'assistant',
          content: 'Sorry, an error occurred while processing your request. Please try again.',
          timestamp: new Date().toISOString()
        });
        this.scrollToBottom();
        console.error(err);
      }
    });
  }

  /** Send message via Anthropic API for page editing */
  private sendPageEditorMessage(userMessage: string): void {
    const ctx = this.activeContext;

    // Build context for Anthropic
    const context: Record<string, string> = {};
    if (ctx.pageId) context['preferencePageId'] = ctx.pageId;
    if (ctx.html) context['currentHtml'] = ctx.html;
    if (ctx.footerHtml) context['currentFooterHtml'] = ctx.footerHtml;
    if (ctx.css) context['currentCss'] = ctx.css;
    if (ctx.script) context['currentScript'] = ctx.script;
    if (ctx.controls && ctx.controls.length > 0) {
      try { context['currentControls'] = JSON.stringify(ctx.controls); } catch (_) {}
    }
    if (ctx.corePreferences && ctx.corePreferences.length > 0) {
      try {
        context['availableCorePreferences'] = JSON.stringify(
          ctx.corePreferences.map((p: any) => ({ id: p.id || '', name: p.name || '', type: p.type || '' }))
        );
      } catch (_) {}
    }
    if (ctx.coreContacts && ctx.coreContacts.length > 0) {
      try {
        context['availableCoreContacts'] = JSON.stringify(
          ctx.coreContacts.map((c: any) => ({ id: c.id || '', name: c.name || '', type: c.type || '' }))
        );
      } catch (_) {}
    }

    const request: ConversationRequest = {
      tenantId: ctx.tenantId || '',
      userMessage,
      context: Object.keys(context).length > 0 ? context : undefined,
      conversationId: this.anthropicConversationId,
      images: this.attachedImages.length > 0 ? this.attachedImages : undefined
    };

    if (ctx.userId) {
      (request as any).userId = ctx.userId;
    }

    this.anthropicService.continueConversation(request).subscribe({
      next: (response) => {
        this.isGenerating = false;

        // Update conversationId
        if (response.conversationId) {
          this.anthropicConversationId = response.conversationId;
        }

        if (response.success) {
          let message = response.assistantMessage || response.response || '';
          message = this.extractStringFromAnthropicMessage(message);

          // Parse generated code
          const generatedCode = this.parseAnthropicGeneratedCode(response);

          // Clean message for display (strip code blocks)
          const displayMessage = generatedCode ? this.stripCodeBlocks(message) : message;

          let assistantContent = displayMessage;

          if (generatedCode && this.hasActualChanges(generatedCode)) {
            this.pendingPageChanges = generatedCode;
            this.pagePreviewTab = 'preview';

            // Use backend-rendered preview if available, otherwise build locally
            const backendPreview = response.previewHtml || response.PreviewHtml;
            if (backendPreview) {
              this.pagePreviewHtml = backendPreview;
            } else {
              this.buildPagePreviewHtml();
            }
            this.showPagePreview = true;
            this.writePreviewIframe();

            // Show what sections were generated
            const sections: string[] = [];
            if (generatedCode.headerHtml) sections.push('Header HTML');
            if (generatedCode.footerHtml) sections.push('Footer HTML');
            if (generatedCode.css) sections.push('CSS');
            if (generatedCode.javascript) sections.push('JavaScript');
            if (generatedCode.controls) sections.push('Controls');

            if (sections.length > 0 && !displayMessage) {
              assistantContent = `I've prepared changes for: **${sections.join(', ')}**. Review the preview and apply or reject.`;
            } else if (sections.length > 0) {
              assistantContent += `\n\nChanges ready: **${sections.join(', ')}**. Review the preview and apply or reject.`;
            }
          }

          this.messages.push({
            role: 'assistant',
            content: assistantContent || 'Done!',
            formattedContent: this.formatMessage(assistantContent || 'Done!'),
            timestamp: new Date().toISOString()
          });

          // Track token usage
          const usage = response.usage || response.Usage;
          if (usage) {
            const totalTokens = usage.totalTokens || usage.TotalTokens || 0;
            if (totalTokens > 0) {
              this.api.aiTokenUsage.addSessionTokens(totalTokens);
              this.api.aiTokenUsage.getUserSummary();
            }
          }
        } else {
          this.messages.push({
            role: 'assistant',
            content: response.errorMessage || 'Failed to process the request.',
            timestamp: new Date().toISOString()
          });
        }

        // Clear attached images after successful send
        this.attachedImages = [];

        this.saveCurrentSession();
        this.refreshTokenCounts();
        this.scrollToBottom();
      },
      error: (err) => {
        this.isGenerating = false;
        this.attachedImages = [];
        this.messages.push({
          role: 'assistant',
          content: 'Sorry, an error occurred while communicating with the AI assistant. Please try again.',
          timestamp: new Date().toISOString()
        });
        this.scrollToBottom();
        console.error('Anthropic error:', err);
      }
    });
  }

  /** Parse generated code from Anthropic response (handles PascalCase and camelCase) */
  private parseAnthropicGeneratedCode(response: any): PageGenerationResult | null {
    const generatedCode = response.generatedCode || response.GeneratedCode;
    if (!generatedCode) return null;

    const sections = generatedCode.sections || generatedCode.Sections;
    if (!sections) return null;

    const result: PageGenerationResult = {
      explanation: generatedCode.explanation || generatedCode.Explanation || ''
    };

    for (const key of Object.keys(sections)) {
      const lowerKey = key.toLowerCase();
      const value = sections[key];
      if (lowerKey === 'headerhtml') result.headerHtml = value;
      else if (lowerKey === 'footerhtml') result.footerHtml = value;
      else if (lowerKey === 'css') result.css = value;
      else if (lowerKey === 'javascript') result.javascript = value;
    }

    if (result.headerHtml || result.footerHtml || result.css || result.javascript) {
      return result;
    }
    return null;
  }

  /** Check if generated code actually differs from the current editor state */
  private hasActualChanges(code: PageGenerationResult): boolean {
    const ctx = this.activeContext;
    const normalize = (s: string | undefined | null) => (s || '').trim();

    if (code.headerHtml && normalize(code.headerHtml) !== normalize(ctx.html)) return true;
    if (code.footerHtml && normalize(code.footerHtml) !== normalize(ctx.footerHtml)) return true;
    if (code.css && normalize(code.css) !== normalize(ctx.css)) return true;
    if (code.javascript && normalize(code.javascript) !== normalize(ctx.script)) return true;
    if (code.controls && code.controls.length > 0) return true;

    return false;
  }

  /** Fallback: build a basic preview locally when the backend doesn't provide PreviewHtml */
  private buildPagePreviewHtml(): void {
    if (!this.pendingPageChanges) {
      this.pagePreviewHtml = '';
      return;
    }
    const ctx = this.activeContext;
    const changes = this.pendingPageChanges;
    const css = changes.css ?? ctx.css ?? '';
    const headerHtml = changes.headerHtml ?? ctx.html ?? '';
    const footerHtml = changes.footerHtml ?? ctx.footerHtml ?? '';
    const js = changes.javascript ?? ctx.script ?? '';

    const rawHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>${css}</style>
</head>
<body>
  ${headerHtml}
  <div id="page-content"></div>
  ${footerHtml}
  <script>${js}<\/script>
</body>
</html>`;
    this.pagePreviewHtml = rawHtml;
  }

  setPreviewTab(tab: 'preview' | 'code'): void {
    this.pagePreviewTab = tab;
    if (tab === 'preview') {
      this.writePreviewIframe();
    }
  }

  /** Write HTML content into the preview iframe using document.write (bypasses Angular sanitization) */
  private writePreviewIframe(): void {
    setTimeout(() => {
      const iframe = this.previewIframe?.nativeElement;
      if (iframe && this.pagePreviewHtml) {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc) {
          doc.open();
          doc.write(this.pagePreviewHtml);
          doc.close();
        }
      }
    }, 50);
  }

  /** Apply pending page changes to the editor via the service */
  applyPageChanges(): void {
    if (!this.pendingPageChanges) return;

    const changes = this.pendingPageChanges;
    this.aiPanelService.emitPageChanges({
      controls: changes.controls,
      headerHtml: changes.headerHtml,
      footerHtml: changes.footerHtml,
      css: changes.css,
      javascript: changes.javascript
    });

    const sections: string[] = [];
    if (changes.headerHtml) sections.push('Header HTML');
    if (changes.footerHtml) sections.push('Footer HTML');
    if (changes.css) sections.push('CSS');
    if (changes.javascript) sections.push('JavaScript');
    if (changes.controls) sections.push('Controls');

    this.messages.push({
      role: 'assistant',
      content: `Applied changes: ${sections.join(', ')}`,
      formattedContent: `<span class="ai-applied-badge"><i class="fas fa-check-circle"></i> Applied: ${sections.join(', ')}</span>`,
      timestamp: new Date().toISOString()
    });

    this.pendingPageChanges = null;
    this.showPagePreview = false;
    this.pagePreviewHtml = '';
    this.saveCurrentSession();
    this.scrollToBottom();
  }

  /** Reject pending page changes */
  rejectPageChanges(): void {
    this.pendingPageChanges = null;
    this.showPagePreview = false;
    this.pagePreviewHtml = '';
    this.isNewPagePreview = false;

    this.messages.push({
      role: 'assistant',
      content: 'Changes discarded. Feel free to try a different approach!',
      timestamp: new Date().toISOString()
    });
    this.saveCurrentSession();
    this.scrollToBottom();
  }

  /** Get preview code for the code tab */
  get pagePreviewCode(): string {
    if (this.isNewPagePreview) return this.pagePreviewHtml;
    if (!this.pendingPageChanges) return '';
    const parts: string[] = [];
    if (this.pendingPageChanges.headerHtml) parts.push(`<!-- Header HTML -->\n${this.pendingPageChanges.headerHtml}`);
    if (this.pendingPageChanges.footerHtml) parts.push(`<!-- Footer HTML -->\n${this.pendingPageChanges.footerHtml}`);
    if (this.pendingPageChanges.css) parts.push(`/* CSS */\n${this.pendingPageChanges.css}`);
    if (this.pendingPageChanges.javascript) parts.push(`// JavaScript\n${this.pendingPageChanges.javascript}`);
    return parts.join('\n\n');
  }

  /** Extract string from Anthropic message (handles objects) */
  private extractStringFromAnthropicMessage(message: any): string {
    if (!message) return '';
    if (typeof message === 'string') return message;
    if (typeof message === 'object') {
      if (message.content && typeof message.content === 'string') return message.content;
      if (message.text && typeof message.text === 'string') return message.text;
      if (Array.isArray(message) && message.length > 0) {
        return this.extractStringFromAnthropicMessage(message[0]);
      }
      return JSON.stringify(message, null, 2);
    }
    return String(message);
  }

  /** Strip code blocks from message for display */
  private stripCodeBlocks(message: string): string {
    if (!message) return '';
    let cleaned = message.replace(/```[\w]*\r?\n[\s\S]*?```/g, '');
    cleaned = cleaned.replace(/```[\w]+[\s\S]*?```/g, '');
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
    return cleaned || message;
  }

  // ── Image Upload (page-editor mode) ──

  triggerFileInput(): void {
    this.fileInput?.nativeElement?.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    Array.from(input.files).forEach(file => {
      if (this.attachedImages.length >= this.maxImages) {
        this.notification.error(`Maximum ${this.maxImages} images allowed`);
        return;
      }
      if (!file.type.startsWith('image/')) {
        this.notification.error('Only image files are allowed');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        this.attachedImages.push({
          base64Data: base64.split(',')[1],
          mediaType: file.type,
          fileName: file.name
        });
      };
      reader.readAsDataURL(file);
    });
    input.value = '';
  }

  removeImage(index: number): void {
    this.attachedImages.splice(index, 1);
  }

  getImageThumbnail(image: AttachedImage): string {
    return `data:${image.mediaType};base64,${image.base64Data}`;
  }

  // ── Speech-to-Text ──

  get speechSupported(): boolean {
    return !!(window as any).webkitSpeechRecognition || !!(window as any).SpeechRecognition;
  }

  toggleSpeechToText(): void {
    if (this.isListening) {
      this.stopListening();
    } else {
      this.startListening();
    }
  }

  private startListening(): void {
    const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    if (!SpeechRecognition) return;

    this.speechRecognition = new SpeechRecognition();
    this.speechRecognition.continuous = false;
    this.speechRecognition.interimResults = true;
    this.speechRecognition.lang = this.detectSpeechLanguage();

    let finalTranscript = '';

    this.speechRecognition.onresult = (event: any) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interim += transcript;
        }
      }
      // Show interim results as they come
      this.aiPrompt = finalTranscript + interim;
    };

    this.speechRecognition.onend = () => {
      this.isListening = false;
      if (finalTranscript) {
        this.aiPrompt = finalTranscript;
      }
    };

    this.speechRecognition.onerror = (event: any) => {
      this.isListening = false;
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        this.notification.error('Speech recognition error: ' + event.error);
      }
    };

    this.isListening = true;
    this.speechRecognition.start();
  }

  private stopListening(): void {
    if (this.speechRecognition) {
      this.speechRecognition.stop();
      this.speechRecognition = null;
    }
    this.isListening = false;
  }

  private detectSpeechLanguage(): string {
    // Detect from browser or default to English
    const browserLang = navigator.language || 'en-US';
    // Support Spanish variants
    if (browserLang.startsWith('es')) return 'es-ES';
    if (browserLang.startsWith('fr')) return 'fr-FR';
    if (browserLang.startsWith('de')) return 'de-DE';
    if (browserLang.startsWith('pt')) return 'pt-BR';
    if (browserLang.startsWith('it')) return 'it-IT';
    if (browserLang.startsWith('ja') || browserLang.startsWith('jp')) return 'ja-JP';
    return 'en-US';
  }

  confirmPageCreation(): void {
    if (!this.aiPage) return;

    this.api.aiPreferencePage.saveFromAi(this.aiPage).subscribe({
      next: (savedPage) => {
        this.notification.success("AI-generated page saved successfully.");
        this.showPreviewPanel = false;

        this.messages.push({
          role: 'assistant',
          content: 'The preference page has been saved successfully! Redirecting you to the page editor.',
          timestamp: new Date().toISOString()
        });

        if (savedPage?.id) {
          this.closePanel();
          this.aiPanelService.navigate('/preference-page/' + savedPage.id);
        }
      },
      error: (err) => {
        this.notification.error("Error saving the AI-generated page.");
        console.error(err);
      }
    });
  }

  private formatRegulationDetail(data: any): string {
    const reg = data.regulation || {};
    let html = `<div class="ai-reg-detail">`;

    // Regulation header
    html += `<div class="ai-reg-header">`;
    html += `<div class="ai-reg-name">${reg['Name'] || 'N/A'}</div>`;
    html += `<div class="ai-reg-fullname">${reg['Full Name'] || ''}</div>`;
    html += `<div class="ai-reg-meta">`;
    html += `<span class="ai-reg-badge ${reg['Enabled'] === 'Yes' ? 'enabled' : 'disabled'}">${reg['Enabled'] === 'Yes' ? 'Active' : 'Inactive'}</span>`;
    if (reg['Priority'] && reg['Priority'] !== 'N/A') html += `<span class="ai-reg-priority">Priority: ${reg['Priority']}</span>`;
    html += `</div>`;
    if (reg['Note'] && reg['Note'] !== 'N/A') html += `<div class="ai-reg-note">${reg['Note']}</div>`;
    html += `</div>`;

    // Derived Permission Types
    const derivedTypes = data.derived_permission_types || [];
    if (derivedTypes.length > 0) {
      html += `<div class="ai-reg-section">`;
      html += `<div class="ai-reg-section-title"><i class="fas fa-shield-alt"></i> Derived Permission Types (${derivedTypes.length})</div>`;
      derivedTypes.forEach((dt: any, i: number) => {
        html += `<div class="ai-card">`;
        html += `<div class="ai-card-number">#${i + 1}</div>`;
        Object.keys(dt).forEach(key => {
          const val = dt[key];
          if (val && val !== 'N/A') {
            html += `<div class="ai-card-row"><span class="ai-card-label">${key}</span><span class="ai-card-value">${val}</span></div>`;
          }
        });
        html += `</div>`;
      });
      html += `</div>`;
    }

    // Processing Purposes
    const purposes = data.processing_purposes || [];
    if (purposes.length > 0) {
      html += `<div class="ai-reg-section">`;
      html += `<div class="ai-reg-section-title"><i class="fas fa-tasks"></i> Processing Purposes (${purposes.length})</div>`;
      purposes.forEach((pp: any, i: number) => {
        html += `<div class="ai-card">`;
        html += `<div class="ai-card-number">#${i + 1}</div>`;
        Object.keys(pp).forEach(key => {
          const val = pp[key];
          if (val !== undefined && val !== null && val !== 'N/A') {
            html += `<div class="ai-card-row"><span class="ai-card-label">${key}</span><span class="ai-card-value">${val}</span></div>`;
          }
        });
        html += `</div>`;
      });
      html += `</div>`;
    }

    // Geo Mappings
    const geoMaps = data.geo_mappings || [];
    if (geoMaps.length > 0) {
      html += `<div class="ai-reg-section">`;
      html += `<div class="ai-reg-section-title"><i class="fas fa-globe"></i> Geo Mappings (${geoMaps.length})</div>`;
      geoMaps.forEach((g: any) => {
        html += `<div class="ai-card">`;
        Object.keys(g).forEach(key => {
          const val = g[key];
          if (val && val !== 'N/A') {
            html += `<div class="ai-card-row"><span class="ai-card-label">${key}</span><span class="ai-card-value">${val}</span></div>`;
          }
        });
        html += `</div>`;
      });
      html += `</div>`;
    }

    // Consent override
    if (data.consent_override_behavior) {
      html += `<div class="ai-reg-section">`;
      html += `<div class="ai-reg-section-title"><i class="fas fa-cog"></i> Settings</div>`;
      html += `<div class="ai-card"><div class="ai-card-row"><span class="ai-card-label">Consent Override</span><span class="ai-card-value">${data.consent_override_behavior}</span></div></div>`;
      html += `</div>`;
    }

    html += `</div>`;
    return html;
  }

  private formatPageDetail(data: any): string {
    const page = data.page || {};
    let html = `<div class="ai-reg-detail">`;

    // Page header
    html += `<div class="ai-reg-header">`;
    html += `<div class="ai-reg-name">${page['Name'] || 'N/A'}</div>`;
    if (page['Description'] && page['Description'] !== 'N/A') html += `<div class="ai-reg-fullname">${page['Description']}</div>`;
    html += `<div class="ai-reg-meta">`;
    html += `<span class="ai-reg-badge ${page['Status']?.toLowerCase() === 'active' ? 'enabled' : 'disabled'}">${page['Status'] || 'N/A'}</span>`;
    if (page['Business Unit'] && page['Business Unit'] !== 'N/A') html += `<span class="ai-reg-priority">BU: ${page['Business Unit']}</span>`;
    if (page['Language'] && page['Language'] !== 'N/A') html += `<span class="ai-reg-priority">Lang: ${page['Language']}</span>`;
    html += `</div>`;
    html += `<div class="ai-reg-meta" style="margin-top:4px">`;
    html += `<span class="ai-reg-priority">Email Search: ${page['Email Search'] || 'N/A'}</span>`;
    html += `<span class="ai-reg-priority">Unsubscribe: ${page['Unsubscribe'] || 'N/A'}</span>`;
    if (page['Redirect URL'] && page['Redirect URL'] !== 'N/A') html += `<span class="ai-reg-priority">Redirect: ${page['Redirect URL']}</span>`;
    html += `</div>`;
    if (page['Created']) html += `<div class="ai-reg-note">Created: ${page['Created']}</div>`;
    html += `</div>`;

    // Core Preferences in page
    const corePrefs = data.core_preferences || [];
    if (corePrefs.length > 0) {
      html += `<div class="ai-reg-section">`;
      html += `<div class="ai-reg-section-title"><i class="fas fa-sliders-h"></i> Core Preferences (${corePrefs.length})</div>`;
      corePrefs.forEach((cp: any, i: number) => {
        html += `<div class="ai-card">`;
        html += `<div class="ai-card-number">#${i + 1}</div>`;
        Object.keys(cp).forEach(key => {
          const val = cp[key];
          if (val && val !== 'N/A') {
            html += `<div class="ai-card-row"><span class="ai-card-label">${key}</span><span class="ai-card-value">${val}</span></div>`;
          }
        });
        html += `</div>`;
      });
      html += `</div>`;
    }

    // Core Contact Fields in page
    const contactFields = data.core_contact_fields || [];
    if (contactFields.length > 0) {
      html += `<div class="ai-reg-section">`;
      html += `<div class="ai-reg-section-title"><i class="fas fa-address-card"></i> Core Contact Fields (${contactFields.length})</div>`;
      contactFields.forEach((cf: any, i: number) => {
        html += `<div class="ai-card">`;
        html += `<div class="ai-card-number">#${i + 1}</div>`;
        Object.keys(cf).forEach(key => {
          const val = cf[key];
          if (val && val !== 'N/A') {
            html += `<div class="ai-card-row"><span class="ai-card-label">${key}</span><span class="ai-card-value">${val}</span></div>`;
          }
        });
        html += `</div>`;
      });
      html += `</div>`;
    }

    if (corePrefs.length === 0 && contactFields.length === 0) {
      html += `<div class="ai-reg-section"><div class="ai-reg-note">No core preferences or contact fields configured in this page.</div></div>`;
    }

    html += `</div>`;
    return html;
  }

  private formatItemDetail(data: any): string {
    const itemType = data.item_type || 'item';
    const item = data.item || {};
    const typeLabels: Record<string, string> = {
      'channel': 'Channel',
      'frequency': 'Frequency',
      'core_preference': 'Core Preference',
      'core_contact_field': 'Core Contact Field'
    };
    const label = typeLabels[itemType] || 'Item';

    let html = `<div class="ai-reg-detail">`;
    html += `<div class="ai-reg-header">`;
    html += `<div class="ai-reg-name">${item['Name'] || item['Label'] || 'N/A'}</div>`;
    if (item['Label'] && item['Label'] !== item['Name'] && item['Label'] !== 'N/A') {
      html += `<div class="ai-reg-fullname">${item['Label']}</div>`;
    }
    html += `<div class="ai-reg-meta">`;
    if (item['Status']) html += `<span class="ai-reg-badge ${item['Status']?.toLowerCase() === 'active' ? 'enabled' : 'disabled'}">${item['Status']}</span>`;
    html += `<span class="ai-reg-priority">${label}</span>`;
    html += `</div>`;
    html += `</div>`;

    // All other fields as details
    html += `<div class="ai-reg-section">`;
    html += `<div class="ai-reg-section-title"><i class="fas fa-info-circle"></i> Details</div>`;
    html += `<div class="ai-card">`;
    Object.keys(item).forEach(key => {
      if (key !== 'Name' && key !== 'Status') {
        const val = item[key];
        if (val !== undefined && val !== null && val !== 'N/A') {
          html += `<div class="ai-card-row"><span class="ai-card-label">${key}</span><span class="ai-card-value">${val}</span></div>`;
        }
      }
    });
    html += `</div>`;
    html += `</div>`;

    html += `</div>`;
    return html;
  }

  private formatAsCards(data: any[], title: string, total: number): string {
    let html = `<div class="ai-cards-header"><strong>${title}</strong><span class="ai-cards-count">${total} record${total !== 1 ? 's' : ''}</span></div>`;
    html += '<div class="ai-cards-list">';
    data.forEach((item, idx) => {
      html += `<div class="ai-card">`;
      html += `<div class="ai-card-number">#${idx + 1}</div>`;
      const keys = Object.keys(item);
      keys.forEach(key => {
        const val = item[key] ?? 'N/A';
        if (val !== 'N/A' && val !== '') {
          html += `<div class="ai-card-row"><span class="ai-card-label">${key}</span><span class="ai-card-value">${val}</span></div>`;
        }
      });
      html += '</div>';
    });
    html += '</div>';
    return html;
  }

  private formatReportAsTable(data: any[]): string {
    const columns = Object.keys(data[0] || {});
    let html = '<table class="ai-report-table"><thead><tr>';
    columns.forEach(col => html += `<th>${col}</th>`);
    html += '</tr></thead><tbody>';
    data.forEach(row => {
      html += '<tr>';
      columns.forEach(col => html += `<td>${row[col] ?? ''}</td>`);
      html += '</tr>';
    });
    html += '</tbody></table>';
    return html;
  }

  private formatMessage(content: string): string {
    // Bold
    let formatted = content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Code blocks
    formatted = formatted.replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>');
    // Inline code
    formatted = formatted.replace(/`(.+?)`/g, '<code>$1</code>');
    // Line breaks
    formatted = formatted.replace(/\n/g, '<br/>');
    return formatted;
  }

  exportToCsv(): void {
    if (!this.dataPreviewRows.length || !this.dataPreviewColumns.length) return;

    const escape = (val: any) => {
      const str = String(val ?? '');
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"` : str;
    };

    let csv = this.dataPreviewColumns.map(escape).join(',') + '\n';
    this.dataPreviewRows.forEach(row => {
      csv += this.dataPreviewColumns.map(col => escape(row[col])).join(',') + '\n';
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.dataPreviewTitle.replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // --- Session persistence ---

  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2, 8);
  }

  private saveCurrentSession(): void {
    if (this.messages.length === 0) return;

    // Strip formattedContent before saving (it's derived)
    const cleanMessages = this.messages.map(({ formattedContent, ...rest }) => rest);

    if (this.currentSessionId) {
      // Update existing session
      const idx = this.chatSessions.findIndex(s => s.id === this.currentSessionId);
      if (idx >= 0) {
        this.chatSessions[idx].messages = cleanMessages;
        this.chatSessions[idx].updatedAt = new Date().toISOString();
        // Move to top
        const session = this.chatSessions.splice(idx, 1)[0];
        this.chatSessions.unshift(session);
      }
    } else {
      // Create new session
      const firstUserMsg = this.messages.find(m => m.role === 'user');
      const title = firstUserMsg
        ? firstUserMsg.content.substring(0, 50) + (firstUserMsg.content.length > 50 ? '...' : '')
        : 'New conversation';
      this.currentSessionId = this.generateSessionId();
      this.chatSessions.unshift({
        id: this.currentSessionId,
        title,
        messages: cleanMessages,
        updatedAt: new Date().toISOString()
      });
    }

    // Trim old sessions
    if (this.chatSessions.length > MAX_SESSIONS) {
      this.chatSessions = this.chatSessions.slice(0, MAX_SESSIONS);
    }

    this.saveSessionsToStorage();
  }

  private loadSessionsFromStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        this.chatSessions = JSON.parse(raw);
      }
    } catch {
      this.chatSessions = [];
    }
  }

  private saveSessionsToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.chatSessions));
    } catch { }
  }

  private refreshTokenCounts(): void {
    this.api.aiTokenUsage.getUserSummary();
    this.refreshSessionTokens();
  }

  private refreshSessionTokens(): void {
    this.api.aiTokenUsage.getSessionTokens(this.sessionStartTime).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res) => { this.sessionTokens = res?.totalTokens ?? 0; },
      error: () => {}
    });
  }

  resetTokenSession(): void {
    this.sessionStartTime = new Date().toISOString();
    localStorage.setItem(STORAGE_SESSION_START, this.sessionStartTime);
    this.sessionTokens = 0;
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.messagesContainer) {
        const el = this.messagesContainer.nativeElement;
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      }
    }, 50);
  }
}
